import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ChannelSheetStylesApiService } from './sheet-styles-API.service';
import { ChannelGroupExcelStyle } from 'icreate/common/models/channel-excel-style';

@Injectable({
    providedIn: 'root',
})
export class ChannelSheetStylesService {
    readonly DEFAULT_FAMILY_FONT = 'Montserrat';
    readonly DEFAULT_FONT_SIZE = '14px';

    private channelGroupStyles: ChannelGroupExcelStyle;
    private hasChangedStyles: boolean = false;
    private excelDefaultStyles;

    constructor(private channelsExcelStylesApi: ChannelSheetStylesApiService) {}

    async setDefaultStyles(defaultStyle, groupId) {
        this.excelDefaultStyles = _.cloneDeep(defaultStyle);
        const savedStyles = await this.channelsExcelStylesApi.getChannelGroupStyle(groupId);
        if (savedStyles && savedStyles.styles) {
            const channelGroupStyles = savedStyles.styles;
            const stylesKeys = _.keys(channelGroupStyles);

            _.forEach(stylesKeys, key => {
                if (channelGroupStyles[key].includes('text-align')) {
                    defaultStyle[key] = defaultStyle[key]
                        .split(';')
                        .filter(style => !style.includes('text-align') && style)
                        .join(';');
                }
                defaultStyle[key] = defaultStyle[key] + channelGroupStyles[key];
            });
            this.channelGroupStyles = {
                id: savedStyles.id,
                groupId: groupId,
                styles: defaultStyle,
            };
            return channelGroupStyles;
        } else {
            this.channelGroupStyles = { groupId: groupId, styles: defaultStyle };
        }
    }

    areStylesUpdated(currentStyles): boolean {
        this.getStylesDifferences(currentStyles);
        return this.hasChangedStyles;
    }

    updateToolbar(selectedCellsStyles, spreadsheet) {
        this.updateBoldToolbarStyle(selectedCellsStyles, spreadsheet);
        this.updateAlignToolbarStyle(selectedCellsStyles, spreadsheet);
        this.updateFamilyFont(selectedCellsStyles, spreadsheet);
        this.updateFontSize(selectedCellsStyles, spreadsheet);
        this.updateTextColor(selectedCellsStyles, spreadsheet);
        this.updateCellColor(selectedCellsStyles, spreadsheet);
    }

    getSelectedCells(coords) {
        const [x1, y1, x2, y2] = coords;
        const selectedCells = [];

        for (let x = Number(x1); x <= Number(x2); x++) {
            if (x === 0) continue;
            const columnLetter = this.columnToLetter(x);
            for (let y = Number(y1); y <= Number(y2); y++) {
                selectedCells.push(columnLetter + (y + 1));
            }
        }

        return selectedCells;
    }

    private columnToLetter(column) {
        let letter = '';
        while (column > 0) {
            let remainder = column % 26;
            letter = String.fromCharCode(65 + remainder) + letter;
            column = Math.floor(column / 26) - 1;
        }
        return letter;
    }

    private getStylePropertyValue(selectedCellsStyles, styleKey, defaultValue) {
        const styles = Object.values(selectedCellsStyles);
        let value;

        styles.forEach((style: string) => {
            const styleEntry = style.split(';').find(s => s.includes(styleKey));
            if (!styleEntry) {
                value = defaultValue;
            } else {
                const extractedStyle = styleEntry.split(':')[0]?.trim();
                if (styleKey != extractedStyle.trim()) return;
                const extractedValue = styleEntry.split(':')[1]?.trim();
                if (!value) {
                    value = extractedValue || defaultValue;
                } else if (extractedValue !== value) {
                    value = defaultValue;
                }
            }
        });

        return value;
    }

    private updateFamilyFont(selectedCellsStyles, spreadsheet) {
        const currentFont = this.getStylePropertyValue(
            selectedCellsStyles,
            ExcelStyleOptions.FontFamily,
            this.DEFAULT_FAMILY_FONT
        );

        const fontFamilySelector = spreadsheet.nativeElement.querySelector('[data-k="font-family"]');
        const fontFamilyOptions: any[] = fontFamilySelector.children;

        for (let i = 0; i < fontFamilyOptions.length; i++) {
            if (fontFamilyOptions[i].innerHTML == currentFont) {
                fontFamilyOptions[i].selected = true;
            } else {
                fontFamilyOptions[i].removeAttribute('selected');
            }
        }
    }

    private updateFontSize(selectedCellsStyles, spreadsheet) {
        const currentFont = this.getStylePropertyValue(
            selectedCellsStyles,
            ExcelStyleOptions.FontSize,
            this.DEFAULT_FONT_SIZE
        );

        const fontFamilySelector = spreadsheet.nativeElement.querySelector('[data-k="font-size"]');
        const fontFamilyOptions: any[] = fontFamilySelector.children;

        for (let i = 0; i < fontFamilyOptions.length; i++) {
            if (fontFamilyOptions[i].innerHTML == currentFont) {
                fontFamilyOptions[i].selected = true;
            } else {
                fontFamilyOptions[i].removeAttribute('selected');
            }
        }
    }

    private updateTextColor(selectedCellsStyles, spreadsheet) {
        const textColor = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.Color);
        const colorTextIcon = document.getElementById('format_color_text');

        if (colorTextIcon) {
            if (textColor && textColor !== ExcelStyleOptions.Undefined) {
                colorTextIcon.style.color = textColor;
            } else {
                colorTextIcon.style.color = 'rgb(0, 0, 0)';
            }
        }
    }

    private updateCellColor(selectedCellsStyles, spreadsheet) {
        const cellColor = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.BackgroundColor);
        const colorCellIcon = document.getElementById('format_color_fill');

        if (colorCellIcon) {
            if (cellColor && cellColor !== ExcelStyleOptions.Undefined) {
                colorCellIcon.style.color = cellColor;
            } else {
                colorCellIcon.style.color = 'rgb(255, 255, 255)';
            }
        }
    }

    private cellHasStyle(selectedCellsStyles, excelStyle) {
        return this.getStylePropertyValue(selectedCellsStyles, excelStyle, ExcelStyleOptions.Undefined);
    }

    private updateBoldToolbarStyle(selectedCellsStyles, spreadsheet) {
        const boldIsSelected = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.FontWeight);
        const boldSelector = spreadsheet.nativeElement.querySelector('#format_bold');

        if (boldSelector) {
            if (boldIsSelected && boldIsSelected !== ExcelStyleOptions.Undefined) {
                boldSelector.style.backgroundColor = 'lightgray';
            } else {
                boldSelector.style.removeProperty(ExcelStyleOptions.BackgroundColor);
            }
        }
    }

    private updateAlignToolbarStyle(selectedCellsStyles, spreadsheet) {
        const textAlign = this.cellHasStyle(selectedCellsStyles, ExcelStyleOptions.TextAlign);

        const alignSelectors = {
            left: spreadsheet.nativeElement.querySelector('#align_left'),
            center: spreadsheet.nativeElement.querySelector('#align_center'),
            right: spreadsheet.nativeElement.querySelector('#align_right'),
        };

        Object.values(alignSelectors).forEach(selector => {
            if (selector) {
                selector.style.removeProperty(ExcelStyleOptions.BackgroundColor);
            }
        });

        if (textAlign && textAlign !== ExcelStyleOptions.Undefined) {
            const selectedAlignSelector = alignSelectors[textAlign];
            if (selectedAlignSelector) {
                selectedAlignSelector.style.backgroundColor = 'lightgray';
            }
        }
    }

    async saveStyleDifferences(currentStyles: Record<string, string> | string) {
        const styleDifferences = this.getStylesDifferences(currentStyles);
        if (this.hasChangedStyles) {
            this.channelGroupStyles.styles = styleDifferences;
            await this.channelsExcelStylesApi.updateChannelGroupStyle(this.channelGroupStyles);
        }
    }

    private getStylesDifferences(currentStyles: Record<string, string> | string): {
        [key: string]: string;
    } {
        const differences: { [key: string]: string } = {};
        const savedGroupStyles = this.channelGroupStyles.styles;
        const allKeys = _.union(_.keys(savedGroupStyles), _.keys(currentStyles));
        this.hasChangedStyles = false;
        allKeys.forEach(cell => {
            const savedCellStyle = this.cleanStyleString(savedGroupStyles[cell]);
            const currentCellStyle = this.cleanStyleString(currentStyles[cell]);
            const excelDefaultStyles = this.cleanStyleString(this.excelDefaultStyles[cell]);
            if (!_.isEqual(savedCellStyle, currentCellStyle)) {
                this.hasChangedStyles = true;
            }
            if (!_.isEqual(excelDefaultStyles, currentCellStyle)) {
                if (currentCellStyle) {
                    const removedSavedStyles = currentCellStyle
                        .split(';')
                        .filter(style => !excelDefaultStyles.includes(style) && style)
                        .join(';');
                    if (removedSavedStyles) {
                        differences[cell] = removedSavedStyles;
                    }
                }
            }
        });

        return differences;
    }

    private cleanStyleString(styleString) {
        if (styleString) {
            return styleString
                .split(';')
                .filter(style => !style.includes('overflow') && style)
                .join(';');
        }
    }
}

export enum ExcelStyleOptions {
    Undefined = 'undefined',
    Color = 'color',
    BackgroundColor = 'background-color',
    FontSize = 'font-size',
    FontFamily = 'font-family',
    TextAlign = 'text-align',
    FontWeight = 'font-weight',
}
